import { Group, Text, Button, Box, Grid, ActionIcon, Flex, Container, Card, Skeleton, Input } from '@mantine/core';
import { IconTrash, IconPencil, IconPlayerPlay, IconPlayerPause, IconCaretUpFilled, IconCaretDownFilled, IconX, IconSearch } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useState, useEffect } from 'react';
import { Link } from "react-router-dom";
import { useDocumentTitle } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { HeaderMenu } from "../../components/HeaderMenu/HeaderMenu";
import { useNavigate } from "react-router-dom";
import InsightsApi from "../../api/InsightsApi";
import { HelpTip } from "../../components/GeneralComponent/GeneralComponent";
import PermissionWrapper from '../../PermissionWrapper';

export function ManageScrollmaps() {
  useDocumentTitle("Visiopt - Insights");
  const navigate = useNavigate();

  const [selectedMemberWebsite, setSelectedMemberWebsite] = useState('');
  const [scrollMapList, setScrollMapList] = useState([]);
  const [sortedScrollmapList, setSortedScrollmapList] = useState([]);
  const [sortKeyName, setSortKeyName] = useState('');
  const [sortingDirection, setSortingDirection] = useState('');
  const [showSkeleton, setShowSkeleton] = useState(false);
  const [ScrollmapButtonShow, setScrollmapButtonShow] = useState('none');

  const handleHeaderMenuComponentChange = (Value:any) => {
    setSelectedMemberWebsite(Value);
  };

  const fetchScrollmapList = () => {
    setShowSkeleton(true);
    InsightsApi.getscrollmaplist({
      system_website_id: selectedMemberWebsite
    })
      .then(response => {
        if (response.ok) {
          return response.json();
        } else {
          if (response.status === 401 || response.status === 400) {
            navigate("/logout");
            setShowSkeleton(false);
          }
        }
      })
      .then(data => {
        if(data.status === 1) {
          setScrollMapList(data.data.scrollmap_list);
          setSortedScrollmapList(data.data.scrollmap_list);
          setShowSkeleton(false);
          if(data.data.scrollmap_button_show === 1){
            setScrollmapButtonShow('block');
          }
        }
      })
      .catch(error => {
        console.error("API Error:", error);
        setShowSkeleton(false);
      });
  }

  const [processingScrollmapId, setProcessingScrollmapId] = useState<any>(null);

  const handleRecordToggle = (pauseStatus:any, scrollmapId:any, scrollmapPageId:any) => {
    if (processingScrollmapId !== null) return; // a scroll map action is already running
    if(pauseStatus === '0') {
      modals.openConfirmModal({
        trapFocus: false,
        title: 'Please confirm your action',
        children: (
          <Text size="sm">
            Are you sure you want to pause this Scroll Map?
          </Text>
        ),
        labels: { confirm: 'Confirm', cancel: 'Cancel' },
        onCancel: () => {console.log('Cancel')},
        onConfirm: () => handleUpdateScrollmapRecord(scrollmapId, pauseStatus, scrollmapPageId)
      });
    }else{
      handleUpdateScrollmapRecord(scrollmapId, pauseStatus, scrollmapPageId);
    }
  };

  const handleUpdateScrollmapRecord = (scrollmapId:any,pauseStatus:any,scrollmapPageId:any) => {
    if (processingScrollmapId !== null) return; // a scroll map action is already running
    setProcessingScrollmapId(scrollmapId);
    InsightsApi.resumescrollmap({
      scrollmap_id: scrollmapId,
      pause_status: pauseStatus,
      page_id: scrollmapPageId
    })
      .then(response => {
        if (response.ok) {
          return response.json();
        } else {
          if (response.status === 401 || response.status === 400) {
            navigate("/logout");
          }
        }
      })
      .then(data => {
        if(data.status === 1){
          fetchScrollmapList();
        }else{
          notifications.show({
            color: 'red',
            icon: <IconX size="1.1rem" />,
            title: 'Error',
            message: data.message,
          });
        }
      })
      .catch(error => {
        console.error("API Error:", error);
      })
      .finally(() => {
        setProcessingScrollmapId(null);
      });
  };

  const updateReport = (scroll_map_id:any) => {
    navigate("/create-scrollmap", { state: { scroll_map_id: scroll_map_id} });
  };

  const deleteScrollmapConfirm = (pauseStatus:any, deleteScrollmapId:any, scrollmapPageId:any) => processingScrollmapId !== null ? undefined : modals.openConfirmModal({
    trapFocus: false,
    title: 'Please confirm your action',
    children: (
      <Text size="sm">
        Are you sure you want to delete this Scroll Map?
      </Text>
    ),
    labels: { confirm: 'Confirm', cancel: 'Cancel' },
    onCancel: () => {console.log('Cancel')},
    onConfirm: () => handleUpdateScrollmapRecord(deleteScrollmapId, pauseStatus, scrollmapPageId)
  });

  const handleScrollMapClick = (scrollmapId:any) => {
    navigate(`/scrollmap-view/${scrollmapId}`);
  };

  useEffect(() => {
    if(selectedMemberWebsite !== '') {
      fetchScrollmapList();
    }
  }, [selectedMemberWebsite]);


  const handleSorting = (keyName: string) => {
    let newDirection = sortingDirection === 'desc' ? 'asc' : 'desc';
    setSortingDirection(newDirection);
    setSortKeyName(keyName);
    
    const sortedScrollmapsList = [...scrollMapList].sort((a, b) => {
      if (!keyName) return 0;

      let valueA = a[keyName];
      let valueB = b[keyName];
      if (keyName === 'create_at') {
        valueA = new Date(a[keyName]);
        valueB = new Date(b[keyName]);
      } else {
        valueA = a[keyName]?.toString().toLowerCase() || '';
        valueB = b[keyName]?.toString().toLowerCase() || '';
      }

      if (valueA < valueB) return newDirection === 'asc' ? -1 : 1;
      if (valueA > valueB) return newDirection === 'asc' ? 1 : -1;
      return 0;
    });
    setSortedScrollmapList(sortedScrollmapsList);
  };

  const handleKeywordChange = (e) => {
    const keyword = e.target.value;

    const filteredList = scrollMapList.filter(data =>
      data.scrollmap_name?.toLowerCase().includes(keyword.toLowerCase())
    );

    setSortedScrollmapList(filteredList);
  };

	return (
		<>
      <HeaderMenu HeaderMenuValue={selectedMemberWebsite} HeaderMenuOnChange={handleHeaderMenuComponentChange}/>
      <Container size="xl">
        <Card shadow="sm" padding="sm" radius="md" withBorder>
          <Group position="apart" mb="xs">
            <Text weight={500}>SCROLL MAPS <HelpTip helpId={253} /></Text>
          </Group>
          <PermissionWrapper permission="operational_access">
          <Button type="button" style={{display:ScrollmapButtonShow}}>
            <Link to="/create-scrollmap" style={{color:"white",textDecoration: "none"}}>
              Create a New Scroll Map
            </Link>
          </Button>
          </PermissionWrapper>
          {scrollMapList.length > 0 && (
          <Box
            p={4}
            pl={20}
            mt={10}
            sx={(theme) => ({
              padding: "10px",
              borderRadius: theme.radius.md,
              fontWeight: "bold",
            })}
          >
          {scrollMapList.some(item => Array.isArray(item) ? item.length > 0 : true) && (
            <Grid gutter="xs">
              <Grid.Col span={2}>
                <Text mt={3} weight={500} onClick={() => handleSorting('scrollmap_name')}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
                    {'Scroll Map Name'}
                    {sortKeyName === 'scrollmap_name' && (
                      sortingDirection === 'asc' ? <IconCaretUpFilled /> : <IconCaretDownFilled />
                    )}
                  </span>
                </Text>
              </Grid.Col>
              <Grid.Col span={2}>
                <Text mt={3} weight={500} onClick={() => handleSorting('funnel_name')}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
                    {'Funnel Name'}
                    {sortKeyName === 'funnel_name' && (
                      sortingDirection === 'asc' ? <IconCaretUpFilled /> : <IconCaretDownFilled />
                    )}
                  </span>
                </Text>
              </Grid.Col>
              <Grid.Col span={4}>
                <Text mt={3} weight={500} onClick={() => handleSorting('page_url')}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
                    {'Page URL'}
                    {sortKeyName === 'page_url' && (
                      sortingDirection === 'asc' ? <IconCaretUpFilled /> : <IconCaretDownFilled />
                    )}
                  </span>
                </Text>
              </Grid.Col>
              <Grid.Col span={2}>
                <Text mt={3} weight={500} onClick={() => handleSorting('create_at')}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
                    {'Date Created'}
                    {sortKeyName === 'create_at' && (
                      sortingDirection === 'asc' ? <IconCaretUpFilled /> : <IconCaretDownFilled />
                    )}
                  </span>
                </Text>
              </Grid.Col>
              <Grid.Col span={2}>
                <Input icon={<IconSearch size={16} />} 
                  placeholder="Search"
                  maxLength={50}
                  autoFocus
                  onChange={handleKeywordChange}
                  style={{ maxWidth: "250px" }}
                />
              </Grid.Col>
            </Grid>
          )}
          </Box>
          )}
          {showSkeleton ? (   
            <Flex direction="column" gap={4}>  
              {Array.from({ length: 15 }).map((_, index) => (
                <Skeleton key={index} height={44} width={1246} mt={10} />
              ))}
            </Flex>
          ) : (
            sortedScrollmapList.map((scrollMap, scrollMapIndex) => (
            <Box
              key={scrollMapIndex}
              p={4}
              pl={20}
              mt={10}
              sx={(theme) => ({
                backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : '#f8f9fa',
                padding: "10px",
                borderRadius: theme.radius.md,
                border: "0.0625rem solid transparent",
                borderColor: theme.colorScheme === 'dark' ? theme.colors.dark[1] : '#dee2e6'
              })}
            >
              <Grid gutter="xs">
                <Grid.Col span={2}>
                  <Text
                    mt={3}
                    onClick={() => handleScrollMapClick(scrollMap.scrollmap_id)}
                    style={{ cursor: 'pointer' }}
                  >
                    {scrollMap.scrollmap_name}
                  </Text>
                </Grid.Col>
                <Grid.Col span={2}>
                  <Text mt={3} >
                    {scrollMap.funnel_name}
                  </Text>
                </Grid.Col>
                <Grid.Col span={4}>
                  <Text mt={3} 
                    style={{
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      cursor: 'pointer'
                    }}
                    title={scrollMap.page_url}
                  >
                    {scrollMap.page_url}
                  </Text>
                </Grid.Col>
                <Grid.Col span={2}>
                  <Text mt={3} >
                    {scrollMap.create_at}
                  </Text>
                </Grid.Col>
                <Grid.Col span={2}>
                  <Flex
                    justify="flex-end"
                    align="center"
                    direction="row"
                    wrap="wrap"
                    gap={2}
                  >
                  <PermissionWrapper permission="operational_access">
                  <ActionIcon
                    variant="default"
                    size="lg"
                    mx={3}
                    title="Pause/Resume"
                    loading={processingScrollmapId === scrollMap.scrollmap_id}
                    disabled={processingScrollmapId !== null}
                    onClick={() => handleRecordToggle(scrollMap.pause_status, scrollMap.scrollmap_id, scrollMap.page_id)}
                  >
                    {scrollMap.pause_status === '1' ? (
                      <IconPlayerPlay size="1.1rem" />
                    ) : (
                      <IconPlayerPause size="1.1rem" />
                    )}
                  </ActionIcon>
                  <ActionIcon
                    variant="default"
                    size="lg"
                    mx={3}
                    title="Edit"
                    disabled={processingScrollmapId !== null}
                    onClick={() => updateReport(scrollMap.scrollmap_id)}
                  >
                    <IconPencil size="1.1rem" />
                  </ActionIcon>
                  <ActionIcon
                    variant="default"
                    size="lg"
                    mx={3}
                    title="Delete"
                    disabled={processingScrollmapId !== null}
                    onClick={() => deleteScrollmapConfirm(2, scrollMap.scrollmap_id, scrollMap.page_id)}
                  >
                    <IconTrash size="1.1rem" />
                  </ActionIcon>
                  </PermissionWrapper>
                  </Flex>
                </Grid.Col>
              </Grid>
            </Box>
          ))
          )}
        </Card>
      </Container>
		</>
	);
}